import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, CheckCircle, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../lib/utils';

function SubscriptionSuccess() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState('checking');
  const hasChecked = useRef(false);

  useEffect(() => {
    if (hasChecked.current) return;
    hasChecked.current = true;

    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    
    if (!sessionId) {
      setStatus('error');
      return;
    }

    let attempts = 0;
    const poll = async () => {
      try {
        const response = await fetch(`${API_URL}/api/checkout/status/${sessionId}`, { credentials: 'include' });
        if (response.ok) {
          const data = await response.json();
          if (data.payment_status === 'paid') {
            await refreshUser();
            setStatus('success');
            return;
          }
        }
        if (++attempts < 5) setTimeout(poll, 2000);
        else setStatus('error');
      } catch {
        setStatus('error');
      }
    };
    poll();
  }, [refreshUser]);

  return (
    <div className="min-h-screen bg-main flex items-center justify-center p-4">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center max-w-md">
        {status === 'checking' && (
          <>
            <Loader2 className="w-16 h-16 text-primary animate-spin mx-auto mb-6" />
            <h1 className="font-heading text-2xl font-bold text-foreground mb-2">Processing...</h1>
            <p className="text-muted">Please wait while we confirm your subscription.</p>
          </>
        )}
        {status === 'success' && (
          <>
            <div className="w-20 h-20 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-emerald-500" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground mb-2">Welcome to Pro!</h1>
            <p className="text-muted mb-8">Enjoy unlimited sprints and all premium features!</p>
            <button onClick={() => navigate('/dashboard')} className="btn-primary">Go to Dashboard</button>
          </>
        )}
        {status === 'error' && (
          <>
            <div className="w-20 h-20 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-6">
              <X className="w-10 h-10 text-red-500" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground mb-2">Something Went Wrong</h1>
            <p className="text-muted mb-8">Please contact support.</p>
            <button onClick={() => navigate('/subscription')} className="btn-secondary">Try Again</button>
          </>
        )}
      </motion.div>
    </div>
  );
}

export default SubscriptionSuccess;
